(function () {
  'use strict';

  // One runtime and one policy, including section-editor remounts.
  if (window.INNERMON_MOTION_CONFIG) return;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var compactViewport = window.matchMedia('(max-width: 749px)');
  var MOTION_CONFIG = Object.freeze({
    cssSignatures: ['puppy-intern', 'jellyfish', 'meerkat', 'parrot', 'boss-dino'],
    spriteSignatures: ['goldfish'],
    signatureDuration: 1100,
    resultDelay: 230,
    spriteLoadTimeout: 4000,
    deliberateTapDistance: 12,
    deliberateTapDuration: 700,
    meerkatRetreatDuration: 650
  });
  window.INNERMON_MOTION_CONFIG = MOTION_CONFIG;
  var activeState = null;
  var gameplayActive = false;
  var instances = new Set();
  var stateByElement = new WeakMap();

  function isUnavailable() {
    return reduceMotion.matches || document.hidden || gameplayActive;
  }

  function usable(state) {
    return !state.disposed && state.element.isConnected && !state.element.hidden && !isUnavailable();
  }

  function releaseMotion(state) {
    if (activeState === state) activeState = null;
  }

  function claimMotion(state, residentPriority) {
    if (!usable(state)) return false;
    // At its already-scheduled beat the locked Snail takes priority; never delay
    // or abandon its sip because a lower-priority hover happened first.
    if (activeState && activeState !== state) {
      if (!residentPriority) return false;
      cancelState(activeState);
    }
    activeState = state;
    return true;
  }

  function createState(element, reset) {
    var state = {
      element: element, reset: reset, epoch: 0, disposed: false,
      timers: new Set(), frames: new Set(), observers: new Set(),
      cleanups: new Set(), disposeCleanups: []
    };
    stateByElement.set(element, state);
    instances.add(state);
    return state;
  }

  function guard(state, epoch, callback) {
    if (state.disposed || state.epoch !== epoch) return;
    if (!usable(state)) { cancelState(state); return; }
    callback();
  }

  function schedule(state, delay, callback) {
    var epoch = state.epoch;
    var timer = window.setTimeout(function () {
      state.timers.delete(timer);
      guard(state, epoch, callback);
    }, delay);
    state.timers.add(timer);
    return timer;
  }

  function nextFrame(state, callback) {
    var epoch = state.epoch;
    var frame = window.requestAnimationFrame(function () {
      state.frames.delete(frame);
      guard(state, epoch, callback);
    });
    state.frames.add(frame);
  }

  function cancelState(state) {
    if (!state || state.disposed) return;
    state.epoch += 1;
    state.timers.forEach(function (timer) { window.clearTimeout(timer); });
    state.timers.clear();
    state.frames.forEach(function (frame) { window.cancelAnimationFrame(frame); });
    state.frames.clear();
    state.observers.forEach(function (observer) { observer.disconnect(); });
    state.observers.clear();
    Array.from(state.cleanups).forEach(function (cleanup) { cleanup(); });
    state.cleanups.clear();
    state.reset();
    releaseMotion(state);
  }

  function disposeState(state) {
    if (!state || state.disposed) return;
    cancelState(state);
    state.disposeCleanups.forEach(function (cleanup) { cleanup(); });
    state.disposeCleanups = [];
    state.disposed = true;
    instances.delete(state);
    stateByElement.delete(state.element);
    delete state.element.dataset.microInteractionReady;
  }

  function listen(state, type, handler, options) {
    state.element.addEventListener(type, handler, options);
    state.disposeCleanups.push(function () { state.element.removeEventListener(type, handler, options); });
  }

  function cancelAll() {
    instances.forEach(cancelState);
  }

  function observeOnce(state, callback) {
    if (!usable(state)) return;
    if (!('IntersectionObserver' in window)) { callback(); return; }
    var epoch = state.epoch;
    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      state.observers.delete(observer);
      guard(state, epoch, callback);
    }, { threshold: 0.05 });
    state.observers.add(observer);
    observer.observe(state.element);
  }

  function randomBetween(minimum, maximum) {
    var min = Number(minimum) || 0;
    var max = Number(maximum) || min;
    if (max < min) max = min;
    return Math.round(min + Math.random() * (max - min));
  }

  function residentCount(name) {
    try { return Number(window.sessionStorage.getItem('innermon-resident-' + name)) || 0; }
    catch (error) { return 0; }
  }

  function recordResident(name) {
    try { window.sessionStorage.setItem('innermon-resident-' + name, String(residentCount(name) + 1)); }
    catch (error) { /* Decorative behavior can continue without storage. */ }
  }

  function showSnailRest(element) {
    var motion = element.querySelector('.innermon-resident-snail__motion');
    element.classList.remove('is-snail-motion', 'is-snail-blinking', 'is-snail-sipping', 'is-snail-settling');
    element.classList.add('is-snail-resting');
    if (!motion) return;
    motion.style.backgroundImage = '';
    motion.style.backgroundPosition = '0 0';
  }

  function preloadSprite(state, source, onReady) {
    var image = new Image();
    var settled = false;
    var epoch = state.epoch;
    var timeout;
    function cleanup() {
      settled = true;
      window.clearTimeout(timeout);
      state.timers.delete(timeout);
      image.onload = null;
      image.onerror = null;
      state.cleanups.delete(cleanup);
    }
    function settle(callback) {
      if (settled) return;
      cleanup();
      guard(state, epoch, callback);
    }
    state.cleanups.add(cleanup);
    timeout = schedule(state, MOTION_CONFIG.spriteLoadTimeout, function () {
      settle(function () { cancelState(state); });
    });
    image.onload = function () { settle(function () { onReady(image); }); };
    image.onerror = function () { settle(function () { cancelState(state); }); };
    image.src = source;
    if (image.complete && image.naturalWidth) settle(function () { onReady(image); });
  }

  function playFrames(state, layer, frameCount, frameDuration, onComplete) {
    nextFrame(state, function () {
      var frame = 0;
      function drawFrame() {
        // Use the current CSS size, including a viewport change during a beat.
        var frameSize = Math.max(1, layer.getBoundingClientRect().width);
        layer.style.backgroundPosition = String(-frame * frameSize) + 'px 0';
        frame += 1;
        schedule(state, frameDuration, frame < frameCount ? drawFrame : onComplete);
      }
      drawFrame();
    });
  }

  function playSnailSprite(state, source, frameCount, frameDuration, name, callback) {
    var element = state.element;
    var motion = element.querySelector('.innermon-resident-snail__motion');
    if (!motion || !source || !claimMotion(state, true)) { cancelState(state); return; }
    preloadSprite(state, source, function () {
      element.classList.remove('is-snail-resting', 'is-snail-blinking', 'is-snail-sipping', 'is-snail-settling');
      element.classList.add('is-snail-motion', 'is-snail-' + name);
      motion.style.backgroundImage = 'url("' + source + '")';
      playFrames(state, motion, frameCount, frameDuration, function () {
        releaseMotion(state);
        if (callback) callback();
        else showSnailRest(element);
      });
    });
  }

  function afterVisibleDelay(state, minimum, maximum, callback) {
    schedule(state, randomBetween(minimum, maximum), function () { observeOnce(state, callback); });
  }

  function initSnail(element) {
    if (stateByElement.has(element)) return;
    element.dataset.microInteractionReady = 'true';
    var state = createState(element, function () { showSnailRest(element); });
    showSnailRest(element);
    if (isUnavailable() || element.dataset.snailMotionComplete === 'true') return;
    var frameDuration = Number(element.dataset.snailFrameDuration) || 125;
    var blinkFrames = Number(element.dataset.snailBlinkFrames) || 8;
    var sipFrames = Number(element.dataset.snailSipFrames) || 28;
    var settleFrames = Number(element.dataset.snailSettleFrames) || 8;
    observeOnce(state, function () {
      afterVisibleDelay(state, element.dataset.snailBlinkDelayMin, element.dataset.snailBlinkDelayMax, function () {
        playSnailSprite(state, element.dataset.snailBlinkSrc, blinkFrames, frameDuration, 'blinking', function () {
          showSnailRest(element);
          afterVisibleDelay(state, element.dataset.snailSipDelayMin, element.dataset.snailSipDelayMax, function () {
            playSnailSprite(state, element.dataset.snailSipSrc, sipFrames, frameDuration, 'sipping', function () {
              playSnailSprite(state, element.dataset.snailSettleSrc, settleFrames, frameDuration, 'settling', function () {
                showSnailRest(element);
                element.dataset.snailMotionComplete = 'true';
              });
            });
          });
        });
      });
    });
  }

  function initMeerkat(element) {
    if (isUnavailable() || compactViewport.matches || stateByElement.has(element) || residentCount('meerkat') >= 2) return;
    element.dataset.microInteractionReady = 'true';
    var state = createState(element, function () {
      element.classList.add('is-motion-cancelled');
      element.classList.remove('is-peeking', 'is-glancing', 'is-retreating');
    });
    schedule(state, Number(element.dataset.idleDelay) || 7000, function () {
      if (compactViewport.matches || !claimMotion(state)) return;
      recordResident('meerkat');
      element.classList.remove('is-motion-cancelled');
      element.classList.add('is-peeking');
      schedule(state, 700, function () {
        element.classList.add('is-glancing');
        schedule(state, 1800, function () {
          element.classList.remove('is-peeking', 'is-glancing');
          element.classList.add('is-retreating', 'is-complete');
          // The existing exit transition is still motion: retain its slot.
          schedule(state, MOTION_CONFIG.meerkatRetreatDuration, function () {
            element.classList.remove('is-retreating');
            releaseMotion(state);
          });
        });
      });
    });
  }

  function resetSignatureState(element) {
    var layer = element.querySelector('.innermon-signature-state-layer');
    element.classList.remove('is-signature-reacting', 'is-signature-state-active');
    if (element.dataset.innermonCandidatePlayback) element.dataset.innermonCandidatePlayback = 'static';
    if (!layer) return;
    layer.style.backgroundImage = '';
    layer.style.backgroundPosition = '0 0';
  }

  function candidatePlayback(element, signature) {
    var candidates = window.INNERMON_MOTION_CANDIDATES;
    return candidates ? candidates.getPlayback(element, signature) : null;
  }

  function signatureAllowed(element, signature) {
    return MOTION_CONFIG.cssSignatures.indexOf(signature) !== -1 ||
      (MOTION_CONFIG.spriteSignatures.indexOf(signature) !== -1 && element.dataset.innermonStateKind === 'sprite') ||
      Boolean(candidatePlayback(element, signature));
  }

  function playSignature(state, signature, delay) {
    var element = state.element;
    if (!usable(state) || activeState === state || element.dataset.signatureComplete === 'true' || !signatureAllowed(element, signature) || !claimMotion(state)) return;
    var candidate = candidatePlayback(element, signature);
    // An enabled flag without a successfully decoded sprite is not playback.
    if (!candidate) element.dataset.signatureComplete = 'true';
    else element.dataset.innermonCandidatePlayback = 'loading';
    function begin() {
      if (element.dataset.innermonStateKind === 'sprite') {
        var layer = element.querySelector('.innermon-signature-state-layer');
        var source = element.dataset.innermonStateSrc;
        if (!layer || !source) { cancelState(state); return; }
        preloadSprite(state, source, function (image) {
          if (candidate) {
            var current = candidatePlayback(element, signature);
            if (!current || current.source !== candidate.source || current.frames !== candidate.frames ||
                image.naturalWidth !== image.naturalHeight * candidate.frames || image.naturalHeight < 64) {
              cancelState(state);
              element.dataset.innermonCandidatePlayback = 'unavailable';
              return;
            }
            element.dataset.signatureComplete = 'true';
            element.dataset.innermonCandidatePlayback = 'playing';
          }
          layer.style.backgroundImage = 'url("' + source + '")';
          element.classList.add('is-signature-state-active');
          playFrames(state, layer, Number(element.dataset.innermonStateFrames) || 1,
            Number(element.dataset.innermonStateFrameDuration) || 140, function () {
              cancelState(state);
              if (candidate) element.dataset.innermonCandidatePlayback = 'completed';
            });
        });
      } else {
        element.classList.add('is-signature-reacting');
        schedule(state, MOTION_CONFIG.signatureDuration, function () { cancelState(state); });
      }
    }
    var startDelay = (delay || 0) + (candidate ? candidate.delay : 0);
    if (startDelay) schedule(state, startDelay, begin);
    else begin();
  }

  function isControl(target, within) {
    var control = target.closest('a[href], button, input, select, textarea, summary, [role="button"]');
    return Boolean(control && within.contains(control));
  }

  function initSignature(element) {
    if (stateByElement.has(element)) return;
    element.dataset.microInteractionReady = 'true';
    var touchStart = null;
    var state = createState(element, function () {
      touchStart = null;
      resetSignatureState(element);
    });
    function moved(event) {
      return Math.hypot(event.clientX - touchStart.x, event.clientY - touchStart.y) > MOTION_CONFIG.deliberateTapDistance ||
        window.scrollY !== touchStart.scrollY || window.scrollX !== touchStart.scrollX;
    }
    function clearPointer() { touchStart = null; }
    listen(state, 'pointerenter', function (event) {
      if (event.pointerType === 'mouse') playSignature(state, element.dataset.innermonSignature);
    }, { passive: true });
    listen(state, 'focusin', function (event) {
      if (isControl(event.target, element)) playSignature(state, element.dataset.innermonSignature);
    });
    // An explicit review control may request another single observation after
    // the prior beat finishes. Hover/focus during playback never restarts it.
    listen(state, 'click', function (event) {
      var trigger = event.target.closest('button[data-innermon-candidate-trigger]');
      if (!trigger || !element.contains(trigger) || activeState === state || isUnavailable() ||
          !candidatePlayback(element, element.dataset.innermonSignature)) return;
      delete element.dataset.signatureComplete;
      playSignature(state, element.dataset.innermonSignature);
    });
    listen(state, 'pointerdown', function (event) {
      touchStart = null;
      if (event.pointerType === 'mouse' || event.isPrimary === false || isControl(event.target, element) || isUnavailable()) return;
      touchStart = { id: event.pointerId, x: event.clientX, y: event.clientY, scrollX: window.scrollX, scrollY: window.scrollY, time: performance.now(), moved: false };
    }, { passive: true });
    listen(state, 'pointermove', function (event) {
      if (touchStart && touchStart.id === event.pointerId && moved(event)) touchStart.moved = true;
    }, { passive: true });
    listen(state, 'pointerup', function (event) {
      if (!touchStart || touchStart.id !== event.pointerId) return;
      var deliberate = !touchStart.moved && !moved(event) && performance.now() - touchStart.time <= MOTION_CONFIG.deliberateTapDuration && !isControl(event.target, element);
      touchStart = null;
      if (deliberate) playSignature(state, element.dataset.innermonSignature);
    }, { passive: true });
    listen(state, 'pointercancel', clearPointer, { passive: true });
    listen(state, 'pointerleave', clearPointer, { passive: true });
  }

  function forMatches(root, selector, callback) {
    if (root.matches && root.matches(selector)) callback(root);
    root.querySelectorAll(selector).forEach(callback);
  }

  function boot(scope) {
    var root = scope || document;
    forMatches(root, '[data-innermon-resident="snail"]', initSnail);
    forMatches(root, '[data-innermon-resident="meerkat"]', initMeerkat);
    forMatches(root, '[data-innermon-signature]', initSignature);
  }

  document.addEventListener('innermon:result-ready', function (event) {
    var element = event.target;
    if (!element.matches('[data-diagnose-result-view]')) return;
    disposeState(stateByElement.get(element));
    delete element.dataset.signatureComplete;
    var state = createState(element, function () { resetSignatureState(element); });
    playSignature(state, element.dataset.character, MOTION_CONFIG.resultDelay);
  });
  document.addEventListener('innermon:result-dismissed', function (event) {
    disposeState(stateByElement.get(event.target));
  });
  document.addEventListener('innermon:gameplay-state', function (event) {
    if (!event.detail || typeof event.detail.active !== 'boolean') return;
    gameplayActive = event.detail.active;
    if (gameplayActive) cancelAll();
    // No boot/replay on release: dropped decorative work must not queue up.
  });
  document.addEventListener('innermon:candidate-flags-changed', function (event) {
    if (!event.target.matches || !event.target.matches('[data-innermon-candidate-review]')) return;
    instances.forEach(function (state) {
      if (event.target.contains(state.element) && state.element.dataset.innermonCandidatePlayback &&
          !candidatePlayback(state.element, state.element.dataset.innermonSignature)) cancelState(state);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    instances.forEach(function (state) { if (event.target.contains(state.element)) disposeState(state); });
  });
  document.addEventListener('visibilitychange', function () { if (document.hidden) cancelAll(); });
  window.addEventListener('pagehide', cancelAll, { passive: true });
  reduceMotion.addEventListener('change', function () { if (reduceMotion.matches) cancelAll(); });
  compactViewport.addEventListener('change', function () {
    if (compactViewport.matches) instances.forEach(function (state) {
      if (state.element.dataset.innermonResident === 'meerkat') cancelState(state);
    });
  });
})();
